import React, { useEffect, useState } from 'react';
import { useKmClient } from '../../../lib/useKmClient.js';

/* ═══════════════════════════════════════════════════════════
   Editor questionari (WYSIWYG) — 24/09. Richiesto in call ("una dashboard
   amministrativa per non dover toccare il codice quando le domande
   cambiano"). Sblocca sul GET /questionari/versione/{id} di Armando
   (24/09) — prima non esisteva modo di leggere il contenuto di una
   bozza, solo di scriverci sopra alla cieca.

   Ciclo §18.5: clone (bozza) → modifica (qui) → validate → publish.
   Le prime due chiamate (clone, publish) e le quattro di modifica
   (sezioni/domande/opzioni) erano già pronte da ieri — mancava solo la
   lettura, ora risolta.

   Priorità del primo giro: le opzioni mancanti. Monaco ha confermato che
   30 domande su 26+ del catalogo comune non hanno alcuna opzione a DB —
   non un bug di caricamento, contenuto che HR non ha mai fornito nel
   foglio originale. Il validatore le segnala già (GET .../validate) —
   qui si può intervenire, domanda per domanda.

   Quattro regole da rispettare (Monaco, 24/09):
   - si scrive solo su una bozza (una versione pubblicata risponde 409)
   - il codice di una domanda non è modificabile — per rinominare si
     elimina e si ricrea
   - le opzioni si sostituiscono sempre tutte insieme (PUT), mai una a una
   - una domanda con risposte già raccolte non si può eliminare (409)
═══════════════════════════════════════════════════════════ */

const T = {
  border: 'rgba(0,0,0,0.12)',
  surface: '#FFFFFF',
  surface2: '#F4F3EF',
  txt: '#1A1916',
  txt2: '#6B6A65',
  txt3: '#A3A19C',
  accent: '#185FA5',
  accentBg: '#E6F1FB',
  accentTxt: '#0C447C',
  greenBg: '#EAF3DE',
  greenTxt: '#27500A',
  redBg: '#FCEBEB',
  redTxt: '#791F1F',
  amberBg: '#FAEEDA',
  amberTxt: '#633806',
};

const TIPI_RISPOSTA = [
  'testo', 'testo_lungo', 'numero', 'data_mese',
  'scelta_singola', 'scelta_multipla', 'lista_testo',
  'voto_1_5', 'matrice_voto_1_5',
];

const HA_OPZIONI = new Set(['scelta_singola', 'scelta_multipla']);

function Badge({ children, bg, fg }) {
  return (
    <span style={{ padding: '2px 8px', borderRadius: 20, fontSize: 11, fontWeight: 600, background: bg, color: fg }}>
      {children}
    </span>
  );
}

// Editor delle opzioni di una domanda — lista libera, ogni riga
// valore+etichetta. PUT sostituisce sempre l'elenco intero (mai una
// modifica puntuale) — coerente con quanto confermato da Monaco.
function OpzioniEditor({ domanda, onSalva, onAnnulla, salvando }) {
  const [righe, setRighe] = useState(
    () => (domanda.opzioni || []).map((o) => ({ valore: o.valore, etichetta: o.etichetta || '' }))
  );

  function aggiorna(i, campo, val) {
    setRighe((prev) => prev.map((r, idx) => (idx === i ? { ...r, [campo]: val } : r)));
  }
  function aggiungi() {
    setRighe((prev) => [...prev, { valore: '', etichetta: '' }]);
  }
  function rimuovi(i) {
    setRighe((prev) => prev.filter((_, idx) => idx !== i));
  }

  return (
    <div style={{ marginTop: 8, padding: 10, borderRadius: 8, background: T.surface2, border: `0.5px solid ${T.border}` }}>
      <div style={{ fontSize: 11.5, color: T.txt2, marginBottom: 8 }}>
        Opzioni — una riga per scelta. L'etichetta è il testo mostrato; se vuota, si usa il valore.
      </div>
      {righe.map((r, i) => (
        <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 6, alignItems: 'center' }}>
          <input
            placeholder="valore"
            value={r.valore}
            onChange={(e) => aggiorna(i, 'valore', e.target.value)}
            style={{ flex: 1, padding: '5px 8px', borderRadius: 6, border: `0.5px solid ${T.border}`, fontSize: 12.5 }}
          />
          <input
            placeholder="etichetta (facoltativa)"
            value={r.etichetta}
            onChange={(e) => aggiorna(i, 'etichetta', e.target.value)}
            style={{ flex: 2, padding: '5px 8px', borderRadius: 6, border: `0.5px solid ${T.border}`, fontSize: 12.5 }}
          />
          <button type="button" onClick={() => rimuovi(i)} title="Rimuovi" style={{ background: 'none', border: 'none', color: T.redTxt, cursor: 'pointer', fontSize: 16, padding: '0 4px' }}>×</button>
        </div>
      ))}
      <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
        <button type="button" onClick={aggiungi} style={{ padding: '5px 10px', borderRadius: 6, border: `0.5px solid ${T.border}`, background: T.surface, fontSize: 11.5, cursor: 'pointer' }}>
          + Aggiungi opzione
        </button>
        <button
          type="button"
          disabled={salvando || righe.some((r) => !r.valore.trim())}
          onClick={() => onSalva(righe.map((r, i) => ({ valore: r.valore.trim(), etichetta: r.etichetta.trim() || null, ordine: i + 1 })))}
          style={{ padding: '5px 10px', borderRadius: 6, border: 'none', background: T.accent, color: '#fff', fontSize: 11.5, fontWeight: 600, cursor: salvando ? 'default' : 'pointer' }}
        >
          {salvando ? 'Salvo…' : 'Salva opzioni'}
        </button>
        <button type="button" onClick={onAnnulla} style={{ padding: '5px 10px', borderRadius: 6, border: 'none', background: 'none', color: T.txt2, fontSize: 11.5, cursor: 'pointer' }}>
          Annulla
        </button>
      </div>
    </div>
  );
}

export default function QuestionariEditorPage() {
  const kmClient = useKmClient('km');

  const [questionari, setQuestionari] = useState([]);
  const [listStatus, setListStatus] = useState('loading');
  const [codiceSelezionato, setCodiceSelezionato] = useState(null);

  const [versioneId, setVersioneId] = useState(null);
  const [contenuto, setContenuto] = useState(null);
  const [contenutoStatus, setContenutoStatus] = useState('idle');

  const [validazione, setValidazione] = useState(null);
  const [cloneLoading, setCloneLoading] = useState(false);
  const [publishLoading, setPublishLoading] = useState(false);
  const [azioneErrore, setAzioneErrore] = useState(null);

  const [domandaInModifica, setDomandaInModifica] = useState(null); // id domanda | null
  const [salvandoOpzioni, setSalvandoOpzioni] = useState(false);

  useEffect(() => {
    kmClient.get('/questionari')
      .then((data) => { setQuestionari(Array.isArray(data) ? data : []); setListStatus('ready'); })
      .catch(() => setListStatus('error'));
  }, [kmClient]);

  function ricaricaContenuto(vId) {
    setContenutoStatus('loading');
    Promise.all([
      kmClient.get(`/questionari/versione/${encodeURIComponent(vId)}`),
      kmClient.get(`/questionari/versione/${encodeURIComponent(vId)}/validate`),
    ])
      .then(([c, v]) => { setContenuto(c); setValidazione(v); setContenutoStatus('ready'); })
      .catch((err) => { setAzioneErrore(err?.detail || 'Errore nel caricamento della bozza.'); setContenutoStatus('error'); });
  }

  async function handleCrea() {
    setCloneLoading(true);
    setAzioneErrore(null);
    try {
      const risultato = await kmClient.post(`/questionari/${encodeURIComponent(codiceSelezionato)}/clone`, {});
      setVersioneId(risultato.versione_id);
      ricaricaContenuto(risultato.versione_id);
    } catch (err) {
      setAzioneErrore(err?.detail || 'Errore nella creazione della bozza.');
    } finally {
      setCloneLoading(false);
    }
  }

  async function handleSalvaOpzioni(domandaId, opzioni) {
    setSalvandoOpzioni(true);
    try {
      await kmClient.put(`/questionari/domande/${encodeURIComponent(domandaId)}/opzioni`, { opzioni });
      setDomandaInModifica(null);
      ricaricaContenuto(versioneId);
    } catch (err) {
      setAzioneErrore(err?.detail || 'Errore nel salvataggio delle opzioni.');
    } finally {
      setSalvandoOpzioni(false);
    }
  }

  async function handlePubblica() {
    setPublishLoading(true);
    setAzioneErrore(null);
    try {
      await kmClient.post(`/questionari/versione/${encodeURIComponent(versioneId)}/publish`, {});
      setVersioneId(null);
      setContenuto(null);
      setValidazione(null);
      // Ricarico la lista, così il questionario ora mostra la versione nuova.
      const lista = await kmClient.get('/questionari');
      setQuestionari(Array.isArray(lista) ? lista : []);
    } catch (err) {
      setAzioneErrore(err?.detail || 'Errore nella pubblicazione.');
    } finally {
      setPublishLoading(false);
    }
  }

  const questionarioCorrente = questionari.find((q) => q.codice === codiceSelezionato);
  const problemiPerDomanda = new Map();
  if (validazione?.problemi) {
    for (const p of validazione.problemi) {
      const key = p.domanda_id || p.domanda_codice;
      if (key) problemiPerDomanda.set(key, p.messaggio || p.problema || JSON.stringify(p));
    }
  }

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', padding: '24px 16px' }}>
      <h1 className="section-title" style={{ marginBottom: 4 }}>Editor questionari</h1>
      <p style={{ color: T.txt2, marginTop: 0, marginBottom: 20, fontSize: 13 }}>
        Modifica domande e opzioni senza intervenire sul codice. Ogni modifica lavora su una bozza —
        il questionario in uso resta invariato finché non pubblichi.
      </p>

      {listStatus === 'loading' && <div style={{ color: T.txt3 }}>Caricamento elenco…</div>}
      {listStatus === 'error' && <div style={{ color: T.redTxt }}>Errore nel caricamento dell'elenco questionari.</div>}

      {listStatus === 'ready' && (
        <div className="card" style={{ marginBottom: 20, padding: 16 }}>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            <select
              value={codiceSelezionato || ''}
              onChange={(e) => { setCodiceSelezionato(e.target.value || null); setVersioneId(null); setContenuto(null); setValidazione(null); }}
              style={{ padding: '7px 10px', borderRadius: 6, border: `0.5px solid ${T.border}`, fontSize: 13, minWidth: 260 }}
            >
              <option value="">— Scegli un questionario —</option>
              {questionari.map((q) => (
                <option key={q.codice} value={q.codice}>{q.codice} — {q.nome}</option>
              ))}
            </select>
            {questionarioCorrente && !versioneId && (
              <button
                type="button"
                onClick={handleCrea}
                disabled={cloneLoading}
                style={{ padding: '7px 14px', borderRadius: 6, border: 'none', background: T.accent, color: '#fff', fontSize: 12.5, fontWeight: 600, cursor: cloneLoading ? 'default' : 'pointer' }}
              >
                {cloneLoading ? 'Creo bozza…' : 'Modifica (crea bozza)'}
              </button>
            )}
          </div>
          {questionarioCorrente && (
            <div style={{ marginTop: 10, fontSize: 12, color: T.txt2 }}>
              Versione pubblicata attuale: n. {questionarioCorrente.versione_numero} ·
              {' '}{questionarioCorrente.n_sezioni} sezioni · {questionarioCorrente.n_domande} domande
            </div>
          )}
        </div>
      )}

      {azioneErrore && (
        <div style={{ padding: 12, borderRadius: 8, background: T.redBg, color: T.redTxt, fontSize: 13, marginBottom: 16 }}>
          {azioneErrore}
        </div>
      )}

      {contenutoStatus === 'loading' && <div style={{ color: T.txt3 }}>Caricamento bozza…</div>}

      {versioneId && contenuto && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 10 }}>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
              <h2 style={{ fontSize: 15, fontWeight: 700, margin: 0 }}>Bozza — {contenuto.nome}</h2>
              {validazione && (
                validazione.valida
                  ? <Badge bg={T.greenBg} fg={T.greenTxt}>Valida</Badge>
                  : <Badge bg={T.amberBg} fg={T.amberTxt}>{validazione.problemi?.length || 0} problem{(validazione.problemi?.length || 0) === 1 ? 'a' : 'i'}</Badge>
              )}
            </div>
            <button
              type="button"
              onClick={handlePubblica}
              disabled={publishLoading || !validazione?.valida}
              title={!validazione?.valida ? 'Risolvi tutti i problemi di validazione prima di pubblicare' : ''}
              style={{ padding: '7px 14px', borderRadius: 6, border: 'none', background: validazione?.valida ? T.accent : T.border, color: validazione?.valida ? '#fff' : T.txt3, fontSize: 12.5, fontWeight: 600, cursor: (publishLoading || !validazione?.valida) ? 'default' : 'pointer' }}
            >
              {publishLoading ? 'Pubblico…' : 'Pubblica'}
            </button>
          </div>

          {contenuto.sezioni.map((sez) => (
            <div key={sez.id} className="card" style={{ marginBottom: 14, padding: 14 }}>
              <div style={{ fontSize: 12.5, fontWeight: 700, color: T.accentTxt, textTransform: 'uppercase', marginBottom: 10 }}>
                {sez.titolo}
              </div>
              {sez.domande.map((d) => {
                const problema = problemiPerDomanda.get(d.id) || problemiPerDomanda.get(d.codice);
                const inModifica = domandaInModifica === d.id;
                return (
                  <div key={d.id} style={{ padding: '10px 0', borderBottom: `0.5px solid ${T.border}` }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
                      <div>
                        <span style={{ fontSize: 11, color: T.txt3, fontFamily: 'monospace', marginRight: 8 }}>{d.codice}</span>
                        <span style={{ fontSize: 13.5 }}>{d.testo}</span>
                        {d.obbligatoria && <span style={{ color: T.redTxt, marginLeft: 4 }}>*</span>}
                      </div>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <span style={{ fontSize: 11, color: T.txt2, background: T.surface2, padding: '2px 6px', borderRadius: 4 }}>{d.tipo_risposta}</span>
                        {HA_OPZIONI.has(d.tipo_risposta) && !inModifica && (
                          <button
                            type="button"
                            onClick={() => setDomandaInModifica(d.id)}
                            style={{ fontSize: 11.5, color: T.accentTxt, background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}
                          >
                            {d.opzioni.length > 0 ? `Modifica opzioni (${d.opzioni.length})` : 'Aggiungi opzioni'}
                          </button>
                        )}
                      </div>
                    </div>
                    {problema && (
                      <div style={{ marginTop: 4, fontSize: 11.5, color: T.amberTxt }}>⚠ {problema}</div>
                    )}
                    {HA_OPZIONI.has(d.tipo_risposta) && !inModifica && d.opzioni.length > 0 && (
                      <div style={{ marginTop: 4, fontSize: 11.5, color: T.txt2 }}>
                        {d.opzioni.map((o) => o.etichetta || o.valore).join(' · ')}
                      </div>
                    )}
                    {inModifica && (
                      <OpzioniEditor
                        domanda={d}
                        salvando={salvandoOpzioni}
                        onSalva={(opzioni) => handleSalvaOpzioni(d.id, opzioni)}
                        onAnnulla={() => setDomandaInModifica(null)}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
